import { useEffect } from "react"
import { toast } from "sonner"

import { Toast } from "@/components/toast"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LcAddress } from "@/domains/core"
import { LockReceivedEvent } from "@/domains/lock/events"
import useEventEmitter from "@/hooks/useEventEmitter"
import useEventHandler from "@/hooks/useEventHandler"

export function EventMonitoring() {
  const scaleApp = useScaleApplication()
  const emitter = useEventEmitter()

  // Global toast handler for EventEmitter (legacy UI notifications)
  useEventHandler({
    emitter,
    event: "*",
    handler: (name, data) => {
      // handle errors
      // absolutely no point in telling the user they just rejected something, it's just noise and dilutes real error significance...
      if (data.description && /user rejected the request/i.test(data.description)) {
        return
      }

      let description = data.description ?? ""
      let variant = data.variant

      if (description && /the transaction may not be processed on the block yet/i.test(description)) {
        variant = "info"
        description =
          "The network is congested and your transaction may not be processed on the block yet. Please wait a moment and try again."
      }

      // Map variant to toastType (waiting -> info, warning -> error)
      const toastType = variant === "waiting" ? "info" : variant === "warning" ? "error" : variant

      const toastNotification = {
        title: data.title,
        description,
        toastType: toastType as "info" | "success" | "error",
        errorCode: data.errorCode,
      }

      console.log("toast event", name, data)

      toast.custom((t) => (
        <Toast
          notification={toastNotification}
          toast={{ dismiss: (id: unknown) => toast.dismiss(id as string | number) }}
          t={t}
        />
      ))
    },
  })

  // EventBus handler for LockReceivedEvent
  useEffect(() => {
    if (!scaleApp) {
      return
    }

    /**
     * New locks look like a transfer in but from the zero address, so ignore them when this happens
     * @param event
     * @returns
     */
    const handleLockReceived = (event: LockReceivedEvent) => {
      const fromAccount = new LcAddress(event.fromAccount)

      if (fromAccount.isZero()) {
        return
      }

      toast.custom((t) => (
        <Toast
          notification={{
            title: "Lock Received",
            description: `You have received lock ID number ${event.lockId} from ${fromAccount.truncate()}`,
            toastType: "success",
          }}
          toast={{ dismiss: (id: unknown) => toast.dismiss(id as string | number) }}
          t={t}
        />
      ))
    }

    scaleApp.EventBus.subscribe(LockReceivedEvent.TYPE, handleLockReceived)

    return () => {
      scaleApp.EventBus.unsubscribe(LockReceivedEvent.TYPE, handleLockReceived)
    }
  }, [scaleApp])

  return null
}
